import React from "react";
import PropTypes from "prop-types";

const CONTROL_HEIGHT = 40;

const toolbarStyle = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  marginBottom: 12,
};

const leftStyle = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  flex: 1,
};

const rightStyle = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  flexShrink: 0,
};

const filterBtnStyle = (active) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  boxSizing: "border-box",
  width: CONTROL_HEIGHT,
  height: CONTROL_HEIGHT,
  border: active ? "1px solid #533dd1" : "1px solid #d2d3d4",
  borderRadius: 9,
  background: active ? "#edeafa" : "#fff",
  color: active ? "#533dd1" : "#394045",
  cursor: "pointer",
  flexShrink: 0,
  boxShadow: "0px 1px 2px rgba(10,13,18,0.05)",
});

const actionBtnStyle = (active) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  boxSizing: "border-box",
  height: CONTROL_HEIGHT,
  padding: "0 16px",
  border: active ? "1px solid #533dd1" : "1px solid #dde1e5",
  borderRadius: 8,
  background: active ? "#edeafa" : "#fff",
  color: active ? "#533dd1" : "#21272a",
  fontSize: 14,
  fontWeight: 500,
  cursor: "pointer",
  boxShadow: "0px 1px 2px rgba(16,24,40,0.05)",
});

const viewToggleWrapperStyle = {
  display: "flex",
  boxSizing: "border-box",
  height: CONTROL_HEIGHT,
  border: "1px solid #e8e7f1",
  borderRadius: 10,
  overflow: "hidden",
  background: "#fff",
  flexShrink: 0,
};

const viewBtnStyle = (active, isRight) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  boxSizing: "border-box",
  width: CONTROL_HEIGHT,
  height: "100%",
  border: "none",
  borderLeft: isRight ? "1px solid #e8e7f1" : "none",
  borderRadius: isRight ? "0 9px 9px 0" : "9px 0 0 9px",
  background: active ? "#edeafa" : "transparent",
  color: active ? "#533dd1" : "#394045",
  cursor: "pointer",
  fontSize: 14,
  flexShrink: 0,
});

function SearchToolbar({
  children,
  showFilters,
  onToggleFilters,
  savedOnly,
  onToggleSavedOnly,
  showSavedToggle = true,
  showMyGroupsButton = true,
  viewMode,
  onViewModeChange,
  groupCount,
  onMyGroupsClick,
}) {
  return (
    // sd-search-toolbar: below the sm breakpoint the stylesheet lets this row wrap, so the
    // Saved / My Groups / view-toggle cluster drops to its own line instead of forcing the
    // page wider than a phone viewport.
    <div className="sd-search-toolbar" style={toolbarStyle}>
      <div style={leftStyle}>
        <div className="sd-search-wrapper" style={{ flex: "0 1 444px", minWidth: 120 }}>
          {children}
        </div>
        <button
          type="button"
          style={filterBtnStyle(showFilters)}
          onClick={onToggleFilters}
          title="Toggle filters"
          aria-pressed={showFilters}
        >
          <i className="fa-solid fa-sliders" style={{ fontSize: 16 }} aria-hidden="true" />
        </button>
      </div>
      <div className="sd-search-toolbar__actions" style={rightStyle}>
        {showSavedToggle && (
          <button
            type="button"
            style={actionBtnStyle(savedOnly)}
            onClick={onToggleSavedOnly}
            title={savedOnly ? "Show all" : "Show saved only"}
            aria-pressed={savedOnly}
          >
            <i className="fa-solid fa-bookmark" style={{ fontSize: 14 }} aria-hidden="true" />
            Saved
          </button>
        )}
        {showMyGroupsButton && (
          <button
            type="button"
            style={
              onMyGroupsClick
                ? actionBtnStyle(false)
                : { ...actionBtnStyle(false), cursor: "not-allowed", opacity: 0.5 }
            }
            onClick={onMyGroupsClick}
            disabled={!onMyGroupsClick}
            title="My Groups"
          >
            <i className="fa-solid fa-users" style={{ fontSize: 14 }} aria-hidden="true" />
            My Groups
            {groupCount > 0 && (
              <span
                style={{
                  background: "#1fa4fc",
                  color: "#fff",
                  borderRadius: "50%",
                  width: 18,
                  height: 18,
                  fontSize: 11,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 600,
                }}
              >
                {groupCount}
              </span>
            )}
          </button>
        )}
        <div style={viewToggleWrapperStyle}>
          <button
            type="button"
            style={viewBtnStyle(viewMode === "grid", false)}
            onClick={() => onViewModeChange("grid")}
            title="Grid view"
            aria-pressed={viewMode === "grid"}
          >
            <i className="fa-solid fa-grid" style={{ fontSize: 16 }} aria-hidden="true" />
          </button>
          <button
            type="button"
            style={viewBtnStyle(viewMode === "list", true)}
            onClick={() => onViewModeChange("list")}
            title="List view"
            aria-pressed={viewMode === "list"}
          >
            <i className="fa-solid fa-list" style={{ fontSize: 16 }} aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
}

SearchToolbar.propTypes = {
  children: PropTypes.node.isRequired,
  showFilters: PropTypes.bool.isRequired,
  onToggleFilters: PropTypes.func.isRequired,
  savedOnly: PropTypes.bool,
  onToggleSavedOnly: PropTypes.func.isRequired,
  showSavedToggle: PropTypes.bool,
  // Off for a host that mounts one search panel on its own, with no saved-groups panel to open.
  showMyGroupsButton: PropTypes.bool,
  viewMode: PropTypes.oneOf(["grid", "list"]).isRequired,
  onViewModeChange: PropTypes.func.isRequired,
  groupCount: PropTypes.number,
  onMyGroupsClick: PropTypes.func,
};

export default SearchToolbar;
